import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import type { MenuActionItem } from 'src/interfaces/ui'
import { getInsuranceProductDeletingId } from 'src/selectors/insuranceProduct'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  insuranceProduct: InsuranceProduct
  onEdit(insuranceProduct: InsuranceProduct): void
  onDelete(insuranceProduct: InsuranceProduct): void
}

const InsuranceProductActionsCellComponent = ({ insuranceProduct, onEdit, onDelete }: Props) => {
  const deletingId = useSelector(getInsuranceProductDeletingId)
  const isDeleting = deletingId === insuranceProduct.id

  const items = useMemo<MenuActionItem[]>(() => {
    return [
      {
        label: 'Edit',
        icon: <GSIcon iconName="gs_pencil" size={20} css={tw`text-shades-1000`} />,
        disabled: isDeleting,
        dataTestId: `insurance-products-list-edit-${insuranceProduct.id}`,
        onClick: () => {
          onEdit(insuranceProduct)
        },
      },
      {
        label: isDeleting ? 'Deleting…' : 'Delete',
        icon: <GSIcon iconName="gs_trash_can" size={20} css={tw`text-critical-500`} />,
        color: 'var(--mui-critical-500)',
        disabled: isDeleting,
        dataTestId: `insurance-products-list-delete-${insuranceProduct.id}`,
        onClick: () => {
          onDelete(insuranceProduct)
        },
      },
    ]
  }, [insuranceProduct, isDeleting, onEdit, onDelete])

  return (
    <RowActionsMenu
      items={items}
      ariaLabel="Insurance product actions"
      dataTestId={`insurance-products-list-actions-${insuranceProduct.id}`}
    />
  )
}

export const InsuranceProductActionsCell = memo(InsuranceProductActionsCellComponent)
